/**
 * How Tonnie works — the story of the move, on demand (Citizen Mobile · D-M1,
 * shared with the phone's screen of the same name). Opened from the account
 * menu at any time; it interrupts nobody, and each chapter leads to its page.
 * Welcome Home tells the same chapters on the page itself.
 */
import {
  BadgeCheck,
  ClipboardCheck,
  GraduationCap,
  Heart,
  type LucideIcon,
  Plane,
} from '@tonnie/assets';
import { HERO_COLORS } from '@tonnie/design-tokens';
import { useTranslation } from '@tonnie/locale';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@tonnie/ui';
import type { TFunction } from 'i18next';
import { Link } from 'wouter';

type Chapter = 'checkup' | 'teleportal' | 'qultureup' | 'care' | 'passport';

const CHAPTERS: ReadonlyArray<{ key: Chapter; icon: LucideIcon; href: string }> = [
  { key: 'checkup', icon: ClipboardCheck, href: '/checkup' },
  { key: 'teleportal', icon: Plane, href: '/teleportal' },
  { key: 'qultureup', icon: GraduationCap, href: '/qultureup' },
  { key: 'care', icon: Heart, href: '/care' },
  { key: 'passport', icon: BadgeCheck, href: '/passport' },
];

function chapterCopy(t: TFunction, key: Chapter): { name: string; title: string; body: string } {
  switch (key) {
    case 'checkup':
      return {
        name: t('heroes.checkup.title'),
        title: t('citizenHome.how.chapters.checkup.title'),
        body: t('citizenHome.how.chapters.checkup.body'),
      };
    case 'teleportal':
      return {
        name: t('heroes.teleportal.title'),
        title: t('citizenHome.how.chapters.teleportal.title'),
        body: t('citizenHome.how.chapters.teleportal.body'),
      };
    case 'qultureup':
      return {
        name: t('heroes.qultureup.title'),
        title: t('citizenHome.how.chapters.qultureup.title'),
        body: t('citizenHome.how.chapters.qultureup.body'),
      };
    case 'care':
      return {
        name: t('heroes.care.title'),
        title: t('citizenHome.how.chapters.care.title'),
        body: t('citizenHome.how.chapters.care.body'),
      };
    case 'passport':
      return {
        name: t('heroes.passport.title'),
        title: t('citizenHome.how.chapters.passport.title'),
        body: t('citizenHome.how.chapters.passport.body'),
      };
    default: {
      const unreachable: never = key;
      throw new Error(`Unhandled chapter ${String(unreachable)}`);
    }
  }
}

export function HowTonnieWorksDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[min(760px,calc(100dvh-2rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
        closeLabel={t('common.close')}
        closeTone="onBrand"
        data-testid="how-tonnie-works-dialog"
      >
        <div className="bg-brand-surface space-y-2 p-6 text-white">
          <p className="text-xs font-semibold uppercase tracking-wider text-white/90">
            {t('citizenHome.how.intro.eyebrow')}
          </p>
          <DialogTitle className="text-2xl font-bold leading-tight text-white">
            {t('citizenHome.how.title')}
          </DialogTitle>
          <DialogDescription className="text-base leading-relaxed text-white/90">
            {t('citizenHome.how.intro.body')}
          </DialogDescription>
        </div>
        <div className="space-y-4 overflow-y-auto p-6">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-foreground">
              {t('citizenHome.how.start.title')}
            </h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t('citizenHome.how.start.body')}
            </p>
          </div>
          <ul className="space-y-2">
            {CHAPTERS.map(({ key, icon: Icon, href }) => {
              const copy = chapterCopy(t, key);
              return (
                <li key={key}>
                  <Link
                    href={href}
                    onClick={() => onOpenChange(false)}
                    className="flex items-start gap-3 rounded-xl border border-border/60 p-4 transition-colors hover:bg-muted/50"
                    data-testid={`how-tonnie-works-${key}`}
                  >
                    <span
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: HERO_COLORS[key].light }}
                      aria-hidden="true"
                    >
                      <Icon className="h-5 w-5" style={{ color: HERO_COLORS[key].text }} />
                    </span>
                    <span className="min-w-0 space-y-0.5">
                      <span
                        className="block text-xs font-semibold uppercase tracking-wider"
                        style={{ color: HERO_COLORS[key].text }}
                      >
                        {copy.name}
                      </span>
                      <span className="block text-base font-semibold text-foreground">
                        {copy.title}
                      </span>
                      <span className="block text-sm leading-relaxed text-muted-foreground">
                        {copy.body}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
